import React, { FunctionComponent, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { IDetectedBarcode, IScannerProps, Scanner } from '@yudiel/react-qr-scanner';
import { useTranslation } from 'react-i18next';
import { SynchronizeEquipmentReturnType } from '90.quickConnect.Models/enums';
import { useStore } from '30.quickConnect.Stores';
import { EntityData } from '90.quickConnect.Models/models';

type ScannerStandaloneProps = Omit<IScannerProps, 'onScan'> & {
  onScanResult: (data: EntityData | undefined, scannedCode?: string) => void;
  schema?: string;
  qCTag?: string;
  searchEquipment?: boolean;
};
const ScannerStandalone: FunctionComponent<ScannerStandaloneProps> = (props: ScannerStandaloneProps) => {
  const {
    EquipmentsStore: { synchronizeEquipmentsWithLockAsync },
  } = useStore();
  const { t: tAxios } = useTranslation('axios');

  const mustBeOfSchema = props.schema ?? '';
  const mustHaveQCTag = props.qCTag ?? '';
  const shouldSearchEquipment = props.searchEquipment ?? true;

  const searchEquipmentByFamily = (family: string): void => {
    if (family && shouldSearchEquipment) {
      synchronizeEquipmentsWithLockAsync(
        family,
        mustBeOfSchema,
        mustHaveQCTag,
        SynchronizeEquipmentReturnType.All,
        undefined,
        tAxios,
      ).then((newData) => props.onScanResult(newData, family));
    }
  };

  const searchEquipmentById = (id?: string): void => {
    if (id && shouldSearchEquipment) {
      //Ajout du tag "##" au code d'équipement pour le différentier côté API du QRcode
      synchronizeEquipmentsWithLockAsync(
        `##${id}`,
        mustBeOfSchema,
        mustHaveQCTag,
        SynchronizeEquipmentReturnType.All,
        undefined,
        tAxios,
      ).then((newData) => props.onScanResult(newData, id));
    }
  };

  const handleScan = (result: IDetectedBarcode[]) => {
    if (result && result.length > 0) {
      const text = result[0].rawValue;
      if (text) {
        if (!shouldSearchEquipment) {
          props.onScanResult(undefined, text);
          return;
        }

        // Format: UUID/UUID
        const familyCodeRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        if (familyCodeRegex.test(text.replace('\\', '/'))) {
          searchEquipmentByFamily(text);
          return;
        }
        searchEquipmentById(text);
      }
    }
  };

  const [scannerError, setScannerError] = useState<string>('');
  return (
    <Box data-cy="ScannerStandalone">
      {<Typography color={'error'}>{scannerError}</Typography>}
      <Scanner
        styles={{ container: { maxWidth: '90vw' } }}
        {...props}
        onScan={handleScan}
        onError={(e) => {
          setScannerError((e as Error).message);
          if (props.onError) {
            props.onError(e);
          }
        }}
      />
    </Box>
  );
};

export default ScannerStandalone;
